import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Link from 'flarum/common/components/Link';
import Select from 'flarum/common/components/Select';
import { t, tt, route, find, syncLocale } from '../i18n';

const SORTS = ['popular', 'name', 'clubs'];

/**
 * /figuren — alle markante figuren (eigenaars, organisatoren, portiers, personeel, vaste gezichten), met zoeken en filters.
 */
export default class FiguresPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    syncLocale();

    this.loading = true;
    this.figures = [];
    this.query = '';
    this.type = '';
    this.club = '';
    this.sort = 'popular';

    app.history.push('lc.figures', tt('figures.title'));
    app.setTitle(tt('figures.title'));
    app.setTitleCount(0);

    const preloaded = app.store.all('lc-figures');
    if (preloaded.length > 20) {
      this.figures = preloaded;
      this.loading = false;
    }

    find('lc-figures').then((figures) => {
      this.figures = figures;
      this.loading = false;
      m.redraw();
    });
    if (app.store.all('lc-clubs').length < 20) find('lc-clubs').then(() => m.redraw());
  }

  view() {
    const list = this.sorted(this.filter(this.figures));
    const clubs = this.clubOptions();
    const types = { '': tt('figures.all_types') };
    ['eigenaar', 'organisator', 'portier', 'personeel', 'mc', 'danser', 'gezicht'].forEach((k) => (types[k] = tt(`figure.type.${k}`)));

    return (
      <div className="LcClubsPage LcArtistsPage LcFiguresPage">
        <section className="LcHero LcHero--artists">
          <div className="container LcHero-inner">
            <div className="LcHero-text">
              <p className="LcEyebrow">The Real Clubhistory</p>
              <h1 className="LcHero-title">{t('figures_page.hero_title', { span: <span /> })}</h1>
              <p className="LcHero-lead">{t('figures.intro', { count: this.figures.length })}</p>
              <div className="LcHero-search">
                <i className="fas fa-search" aria-hidden="true" />
                <input
                  className="FormControl"
                  type="search"
                  placeholder={tt('figures.search_placeholder')}
                  value={this.query}
                  oninput={(e) => (this.query = e.target.value)}
                  aria-label={tt('figures.search_placeholder')}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="container LcArchive">
          <div className="LcFilters">
            <Select value={this.type} options={types} onchange={(value) => (this.type = value)} />
            <Select value={this.club} options={{ '': tt('figures.all_clubs'), ...clubs }} onchange={(value) => (this.club = value)} />
            <Select value={this.sort} options={Object.fromEntries(SORTS.map((k) => [k, tt(`common.sort.${k}`)]))} onchange={(value) => (this.sort = value)} />
            <span className="LcFilters-count">{t('figures.count', { count: list.length })}</span>
          </div>

          {this.loading ? (
            <LoadingIndicator />
          ) : list.length ? (
            <ul className="LcGrid LcGrid--small">{list.map((f) => this.card(f))}</ul>
          ) : (
            <p className="LcEmpty">{t('figures.no_results')}</p>
          )}
        </section>
      </div>
    );
  }

  card(figure) {
    const names = (figure.clubSlugs() || []).map((s) => this.clubName(s)).filter(Boolean);
    return (
      <li key={figure.slug()} className={'LcCard' + (figure.hasStory() ? ' LcCard--featured' : '')}>
        <Link href={route('lc.figure', { slug: figure.slug() })} className="LcCard-link">
          <h3 className="LcCard-name">{figure.name()}</h3>
          <span className="LcCard-years LcCard-role">{figure.role() || t(`figure.type.${figure.type()}`)}</span>
          {names.length ? (
            <span className="LcCard-place">
              <i className="fas fa-map-marker-alt" aria-hidden="true" /> {names.slice(0, 3).join(', ')}
              {names.length > 3 ? ` +${names.length - 3}` : ''}
            </span>
          ) : null}
          {figure.forumMentions() ? (
            <span className="LcCard-meta">
              <i className="far fa-comments" aria-hidden="true" /> {t('artists.mentions', { count: figure.forumMentions() })}
            </span>
          ) : null}
        </Link>
      </li>
    );
  }

  clubName(slug) {
    const club = app.store.all('lc-clubs').find((c) => c.slug() === slug);
    return club ? club.name() : null;
  }

  /** Clubs die bij minstens één figuur voorkomen. */
  clubOptions() {
    const slugs = new Set();
    this.figures.forEach((f) => (f.clubSlugs() || []).forEach((s) => slugs.add(s)));
    return Object.fromEntries(
      Array.from(slugs)
        .map((s) => [s, this.clubName(s) || s])
        .sort((a, b) => a[1].localeCompare(b[1], 'nl'))
    );
  }

  filter(figures) {
    const q = this.query.trim().toLowerCase();
    return figures.filter((f) => {
      if (q && !f.searchText().includes(q) && !(f.clubSlugs() || []).some((s) => (this.clubName(s) || '').toLowerCase().includes(q))) return false;
      if (this.type && f.type() !== this.type) return false;
      if (this.club && !(f.clubSlugs() || []).includes(this.club)) return false;
      return true;
    });
  }

  sorted(figures) {
    const list = figures.slice();
    const byName = (a, b) => a.name().localeCompare(b.name(), 'nl');
    if (this.sort === 'name') list.sort(byName);
    if (this.sort === 'clubs') list.sort((a, b) => (b.clubSlugs() || []).length - (a.clubSlugs() || []).length || byName(a, b));
    if (this.sort === 'popular') list.sort((a, b) => (b.forumMentions() || 0) - (a.forumMentions() || 0) || byName(a, b));
    return list;
  }
}
